// ─────────────────────────────────────────────────────────────
// Create a payment link. Wrap route in
// <ServiceRoute serviceKey="payment_link">.
//
// LIGHT/DARK MODE PASS — migrated from raw Tailwind slate/emerald
// classes + hardcoded 'Space Grotesk' font string onto the design-
// token system used throughout the rest of the app (bg-bg, bg-surface,
// text-primary, var(--color-brand), font-display, etc.). Previously
// had zero dark-mode support — every color was a fixed light-theme
// hex value with no response to data-theme="dark".
// ─────────────────────────────────────────────────────────────
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast }       from "react-toastify";
import { apiClient }   from "../../api/apiServices";

export default function CreatePaymentLinkPage() {
  const navigate = useNavigate();
  const [title, setTitle]       = useState("");
  const [description, setDesc]  = useState("");
  const [openAmount, setOpen]   = useState(true);
  const [amount, setAmount]     = useState("");
  const [reusable, setReusable] = useState(true);
  const [expiresAt, setExpiry]  = useState("");
  const [loading, setLoading]   = useState(false);

  const create = async () => {
    if (!title.trim()) return toast.error("Give your link a title");
    if (!openAmount && (!amount || Number(amount) <= 0)) return toast.error("Enter a fixed amount or choose 'let sender decide'");
    setLoading(true);
    try {
      const r = await apiClient.post("/mtrade/payment-links", {
        title: title.trim(),
        description: description.trim(),
        amount: openAmount ? null : Number(amount),
        reusable,
        expiresAt: expiresAt || null,
      });
      if (r.data.status) { toast.success("Link created"); navigate("/payment-link"); }
      else toast.error(r.data.message);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not create link");
    } finally { setLoading(false); }
  };

  const minDate = new Date(Date.now() + 86400000).toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-bg pb-10 font-display">
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
            <i className="ri-arrow-left-line text-secondary text-lg" />
          </button>
          <h1 className="text-primary font-bold text-lg">New Payment Link</h1>
        </div>
      </div>

      <div className="px-5 pt-5 pb-10 max-w-lg mx-auto space-y-5">
        <div>
          <label className="text-secondary text-sm font-semibold mb-2 block">Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Payment for design work"
            className="w-full bg-surface border-2 border-border-strong rounded-2xl px-4 py-3.5 text-sm text-primary outline-none focus:border-brand placeholder:text-faint transition-colors" />
        </div>

        <div>
          <label className="text-secondary text-sm font-semibold mb-2 block">Description (optional)</label>
          <input value={description} onChange={e => setDesc(e.target.value)} placeholder="What is this payment for?"
            maxLength={280}
            className="w-full bg-surface border-2 border-border-strong rounded-2xl px-4 py-3.5 text-sm text-primary outline-none focus:border-brand placeholder:text-faint transition-colors" />
        </div>

        {/* Amount mode */}
        <div className="bg-surface border border-border rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary font-semibold text-sm">Let sender enter amount</p>
              <p className="text-muted text-xs">Off = you set a fixed amount</p>
            </div>
            <button onClick={() => setOpen(v => !v)}
              className="w-12 h-7 rounded-full transition-colors relative"
              style={{ background: openAmount ? "var(--color-brand)" : "var(--color-border-strong)" }}>
              <span className="absolute top-1 w-5 h-5 bg-white rounded-full transition-all" style={{ left: openAmount ? "1.5rem" : "0.25rem" }} />
            </button>
          </div>
          {!openAmount && (
            <div className="bg-surface-muted border border-border-strong rounded-xl flex items-center px-3">
              <span className="text-muted font-bold mr-2">₦</span>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Fixed amount"
                className="flex-1 py-2.5 outline-none text-primary text-sm bg-transparent placeholder:text-faint" />
            </div>
          )}
        </div>

        {/* Reusable */}
        <div className="bg-surface border border-border rounded-2xl p-4 flex items-center justify-between">
          <div>
            <p className="text-primary font-semibold text-sm">Reusable link</p>
            <p className="text-muted text-xs">Off = closes after one payment</p>
          </div>
          <button onClick={() => setReusable(v => !v)}
            className="w-12 h-7 rounded-full transition-colors relative"
            style={{ background: reusable ? "var(--color-brand)" : "var(--color-border-strong)" }}>
            <span className="absolute top-1 w-5 h-5 bg-white rounded-full transition-all" style={{ left: reusable ? "1.5rem" : "0.25rem" }} />
          </button>
        </div>

        <div>
          <label className="text-secondary text-sm font-semibold mb-2 block">Expiry date (optional)</label>
          <input type="date" value={expiresAt} min={minDate} onChange={e => setExpiry(e.target.value)}
            className="w-full bg-surface border-2 border-border-strong rounded-2xl px-4 py-3.5 text-sm text-primary outline-none focus:border-brand transition-colors" />
        </div>

        <button onClick={create} disabled={loading}
          className="w-full bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2">
          {loading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <><i className="ri-link" />Create Link</>}
        </button>
      </div>
    </div>
  );
}